import type {
  Bonus,
  ComparisonRow,
  Faq,
  ShortQuote,
  Stat,
  Testimonial,
  Track,
  Volume,
} from '../types/content';

export const OFFER_DEADLINE_MS = Date.now() + (11 * 3600 + 13 * 60 + 35) * 1000;

export const CHECKOUT_URL = 'https://el-profe-de-musica.impultienda.ar/checkout';

export const PRICE_LABEL = '$27.990';
export const PRICE_NUMERIC = 27990;

export const WHATSAPP_NUMBER = '5491123745788';
export const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  'Hola, quiero hablar con un asesor sobre el pack de El Profe de Guitarra.',
)}`;

export const BUNDLE_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/3n7Pk62ZweGMTpy25TXZTV/ChatGPT_Image_9_sept_2026,_11_52_24_a.m..png';

export const heroBullets: string[] = [
  '6 volúmenes completos',
  '6 bonos exclusivos',
  '+300 páginas listas para imprimir',
  '+150 ejercicios y rutinas',
  '100% PDF — enviado a tu email',
  'De principiante a avanzado',
];

export const proofStats: Stat[] = [
  { value: '300+', label: 'Páginas PDF listas para imprimir' },
  { value: '6 + 6', label: 'Volúmenes y bonos incluidos' },
  { value: '50+', label: 'Rutinas y planes de clase' },
  { value: 'Niños a adultos', label: 'De principiante a avanzado' },
];

export const shortQuotes: ShortQuote[] = [
  {
    quote:
      'Antes de tener este pack pasaba 5 horas por semana solo buscando qué enseñar. Ahora imprimo y enseño.',
    author: 'Sarah J. · Profesora de guitarra',
  },
  {
    quote:
      'Mis principiantes tocan música real en la tercera semana. Solo las rutinas de púa alternada ya valían el precio.',
    author: 'Daniel M. · Profesor de guitarra',
  },
  {
    quote:
      'Todos los profes de mi academia trabajan con el mismo plan. Los padres finalmente ven el progreso.',
    author: 'Laura C. · Directora de academia',
  },
];

export const painPoints: string[] = [
  'Pasás horas buscando material en Pinterest y YouTube que parezca profesional, y solo encontrás cosas mediocres',
  'Repetís los mismos ejercicios porque ya te quedaste sin material nuevo',
  'No tenés una progresión técnica lógica planificada para todo el año',
  'Perdés alumnos después del segundo mes porque se aburren con métodos viejos y polvorientos',
  'Llegás sin preparación y con estrés a cada clase',
  'Sos un gran guitarrista, pero estás agotado de improvisar cada clase',
];

export const systemStats: Stat[] = [
  { value: '300+', label: 'Páginas PDF prácticas' },
  { value: '150+', label: 'Ejercicios listos para enseñar' },
  { value: '50+', label: 'Rutinas semana a semana' },
  { value: '6', label: 'Volúmenes completos' },
];

export const tracks: Track[] = [
  { number: '01', label: 'Independencia y digitación' },
  { number: '02', label: 'Saltos de cuerda y púa alternada' },
  { number: '03', label: 'Ritmo, tresillos y galope' },
  { number: '04', label: 'Ligados: hammer-ons y pull-offs' },
  { number: '05', label: 'Slides, velocidad y técnica avanzada' },
  { number: '06', label: 'Resistencia y automatización' },
];

export const volumes: Volume[] = [
  {
    number: '01',
    pages: '50+ páginas PDF',
    title: 'Independencia, Digitación y Control de Dedos',
    description:
      'Araña clásica, 1-2-3-4, inversiones y permutaciones 1-3-2-4 / 4-2-3-1. La base técnica: coordinación y sonido limpio desde la primera clase.',
    tags: ['Araña clásica', '1-2-3-4 e inversiones', 'Permutaciones', 'Control limpio'],
  },
  {
    number: '02',
    pages: '50+ páginas PDF',
    title: 'Saltos de Cuerda y Púa Alternada',
    description:
      'Saltos entre cuerdas con precisión y apagado, coordinación de ambas manos y corcheas con púa alternada trabajando el control del ataque.',
    tags: ['Saltos de cuerda', 'Púa alternada', 'Apagado', 'Control del ataque'],
  },
  {
    number: '03',
    pages: '50+ páginas PDF',
    title: 'Ritmo, Tresillos, Cromatismo y Galope',
    description:
      'Tresillos y subdivisión rítmica, escala cromática, galope y acentos, con estabilidad real trabajada al metrónomo.',
    tags: ['Tresillos', 'Escala cromática', 'Galope', 'Metrónomo'],
  },
  {
    number: '04',
    pages: '50+ páginas PDF',
    title: 'Ligados, Hammer-ons y Pull-offs',
    description:
      'Ligados ascendentes y descendentes con dinámica pareja, ida y vuelta, definición del sonido y combinaciones progresivas.',
    tags: ['Hammer-ons', 'Pull-offs', 'Dinámica pareja', 'Combinaciones'],
  },
  {
    number: '05',
    pages: '50+ páginas PDF',
    title: 'Slides, Velocidad y Técnica Avanzada',
    description:
      'Deslizamientos ascendentes y descendentes, combinaciones slide + ligado, semicorcheas, velocidad progresiva y pentatónica: 50 rutinas de las semanas 5 a 8.',
    tags: ['Slides', 'Slide + ligado', 'Semicorcheas', 'Pentatónica'],
  },
  {
    number: '06',
    pages: '50+ páginas PDF',
    title: 'Resistencia, Coordinación y Automatización',
    description:
      'Resistencia técnica, independencia de manos, circuitos largos, rampas de metrónomo y patrones combinados hasta automatizar el movimiento.',
    tags: ['Circuitos largos', 'Rampas de BPM', 'Independencia de manos', 'Automatización'],
  },
];

export const bonuses: Bonus[] = [
  {
    number: '01',
    value: '$97.000 · Gratis',
    title: 'Diagnóstico Express del Alumno en 10 Minutos',
    description:
      'Una ficha imprimible para detectar en la primera clase el nivel real, los vicios de técnica y los objetivos de cualquier alumno nuevo — en solo 10 minutos.',
    cover: 'https://cdn.magicpatterns.com/uploads/siSKS5iFmizZjgRgLfcP7Q/guitara_1.webp',
  },
  {
    number: '02',
    value: '$97.000 · Gratis',
    title: 'Tracker de Progreso BPM y Tareas Semanales',
    description:
      'Planillas imprimibles para registrar el BPM de cada ejercicio y las tareas de la semana, para que el alumno vea su progreso en números.',
    cover: 'https://cdn.magicpatterns.com/uploads/ueNdadiXoZT7s4kjG3yKjX/guitarra_3.webp',
  },
  {
    number: '03',
    value: '$127.000 · Gratis',
    title: 'Biblioteca de Calentamientos Rápidos para Cada Clase',
    description:
      'Más de 30 calentamientos de 5 minutos para dedos, muñeca y púa, listos para abrir cualquier clase sin pensarlo.',
    cover: 'https://cdn.magicpatterns.com/uploads/bTWYDzBE47mNp6E1aRUF4v/guitarra_2.webp',
  },
  {
    number: '04',
    value: '$47.000 · Gratis',
    title: '50 Retos Rápidos para Motivar al Alumno',
    description:
      '50 retos cortos e imprimibles que convierten la práctica en casa en un juego y hacen que el alumno vuelva con algo listo.',
    cover: 'https://cdn.magicpatterns.com/uploads/gURHZU838xWP2t1JfawLAu/guitara_4.webp',
  },
  {
    number: '05',
    value: '$150.000 · Gratis',
    title: 'Guía de Correcciones Rápidas para Profesores de Guitarra',
    description:
      'Los errores más comunes de postura, mano izquierda y ritmo, con la corrección exacta que funciona en el momento.',
    cover: 'https://cdn.magicpatterns.com/uploads/n3G9W3j11HJSwP1hNJX9Bu/guitarra_5.webp',
  },
  {
    number: '06',
    value: '$67.000 · Gratis',
    title: '10 Tablaturas Listas de Regalo',
    description:
      'Diez tablaturas completas y listas para imprimir: repertorio inmediato para llevar a la clase, del riff simple a la pieza entera.',
    cover:
      'https://cdn.magicpatterns.com/uploads/wfEkgroByfqxepwtG57Ni1/ChatGPT_Image_9_sept_2026,_11_47_16_a.m..png',
  },
];

export const offerIncludes: string[] = [
  '6 volúmenes completos de técnica',
  '6 guías bonus exclusivas — gratis',
  '10 tablaturas listas de regalo',
  '+300 páginas listas para imprimir',
  '100% PDF — sin videos ni audiolibros',
  'Enviado a tu email justo después de comprar',
];

export const testimonials: Testimonial[] = [
  {
    initials: 'SJ',
    name: 'Sarah J.',
    role: 'Profesora de guitarra',
    quote:
      'Antes de tener este pack pasaba 5 horas por semana solo buscando qué enseñar. Ahora imprimo y enseño. Mis alumnos progresan más rápido y no perdí un solo alumno en 3 meses.',
    photo: 'https://cdn.magicpatterns.com/uploads/o1vCX9mqjX6FG9PzvxZFAH/im_2.jpg',
  },
  {
    initials: 'DM',
    name: 'Daniel M.',
    role: 'Profesor de guitarra eléctrica',
    quote:
      'Las rutinas de púa alternada y saltos de cuerda hicieron que mis principiantes toquen riffs reales en la tercera semana. Nada de lo que compré antes logró eso.',
  },
  {
    initials: 'LC',
    name: 'Laura C.',
    role: 'Directora de academia de música',
    quote:
      'Todos los profes de mi academia trabajan ahora con el mismo material. Solo la licencia de impresión ya lo hizo una decisión obvia.',
    photo: 'https://cdn.magicpatterns.com/uploads/nSfMxdn4sXfBW2HcZ4HiaB/img_3.jpg',
  },
  {
    initials: 'RT',
    name: 'Rachel T.',
    role: 'Dueña de estudio particular',
    quote:
      'El tracker de BPM cambió mis reuniones con los padres. Puedo mostrar progreso concreto en vez de decir «va bien».',
    photo: 'https://cdn.magicpatterns.com/uploads/oxGJUaUnHiYnLkMCndaBUu/img_4.jpg',
  },
  {
    initials: 'MO',
    name: 'Marco O.',
    role: 'Profesor de guitarra acústica',
    quote:
      'Los ligados y los slides estaban explicados justo como los enseño, así que mi planificación del domingo se resuelve en minutos.',
  },
];

export const faqs: Faq[] = [
  {
    question: '¿Es un libro físico? ¿En qué formato viene?',
    answer:
      'Es 100% digital y 100% PDF: no hay videos ni audiolibros. Cada volumen y cada bono es un PDF listo para imprimir que recibís por email, así que podés descargar e imprimir todo de inmediato.',
  },
  {
    question: '¿Funciona para todas las edades y niveles?',
    answer:
      'Sí. La progresión va del control básico de dedos hasta resistencia y velocidad avanzada, así que sirve tanto para chicos que recién arrancan como para adolescentes y adultos de nivel intermedio o avanzado.',
  },
  {
    question: '¿Puedo usarlo en mi academia de música?',
    answer:
      'Sí. Incluye licencia de impresión ilimitada para tu estudio o academia: todos tus profes pueden trabajar con el mismo material y las mismas rutinas.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Apenas completás la compra te llega un email con los enlaces de descarga de los 6 volúmenes y los 6 bonos. No hay plataforma para loguearse ni esperas.',
  },
  {
    question: '¿Y si no quedo del todo satisfecho?',
    answer:
      'Tenés 7 días de garantía. Escribinos un email y te devolvemos el 100% de lo que pagaste, sin formularios y sin preguntas. Los bonos te los quedás.',
  },
];

export const comparison: ComparisonRow[] = [
  {
    without: '5+ horas por semana buscando ejercicios',
    with: 'Imprimí y enseñá · Todo ya preparado',
  },
  {
    without: 'Clases improvisadas · Sin progresión clara',
    with: '+300 páginas PDF · Progresión lógica semana a semana',
  },
  {
    without: 'Alumnos aburridos con métodos polvorientos',
    with: 'Rutinas, retos y tablaturas que ellos te piden',
  },
  {
    without: 'Padres preguntando «¿dónde están los resultados?»',
    with: 'Tracker de BPM e informes de progreso listos',
  },
  {
    without: 'Domingos perdidos armando material',
    with: 'Todo tu año planificado · Domingos recuperados',
  },
];
